/**
 * Relationship condition renderer.
 * Matches ~/code/ui condition-editor-v2/nodes/relationship/view.gts
 *
 * Renders: Relationship to **objectName** exists/does not exist
 * with optional split sub-filters for object vs relationship attributes.
 */

import type { RelationshipNode, ConditionFilter } from '../../types';
import { ConditionCard } from '../condition-card';
import { Bold, SubFilters } from '../condition-parts';

function splitFilters(filters: ConditionFilter[] | undefined): {
  objectFilters: ConditionFilter[];
  relationshipFilters: ConditionFilter[];
} {
  if (filters == null || filters.length === 0) {
    return { objectFilters: [], relationshipFilters: [] };
  }

  const objectFilters: ConditionFilter[] = [];
  const relationshipFilters: ConditionFilter[] = [];

  for (const filter of filters) {
    if (filter.sourceType === 'object') {
      objectFilters.push(filter);
    } else {
      relationshipFilters.push(filter);
    }
  }

  return { objectFilters, relationshipFilters };
}

interface RelationshipConditionProps {
  node: RelationshipNode;
}

export function RelationshipCondition({ node }: RelationshipConditionProps) {
  const name = node.objectTypeName ?? (node.objectTypeId != null ? `object #${node.objectTypeId}` : 'object');
  const { objectFilters, relationshipFilters } = splitFilters(node.filters);

  return (
    <ConditionCard node={node}>
      Relationship to <Bold>{name}</Bold> {node.inverse ? 'does not exist' : 'exists'}
      <SubFilters filters={objectFilters} filtersOperator={node.filtersOperator} label={`${name} attributes`} />
      <SubFilters
        filters={relationshipFilters}
        filtersOperator={node.filtersOperator}
        label="relationship attributes"
      />
    </ConditionCard>
  );
}
